import React, { memo, useMemo } from "react";
import Select from "components/Select";
import { useGetStates } from "api/states/service";
import {
  normalizeStatesFromApi,
  filterStateSelectOption,
} from "utils/normalizeStatesApi";

/**
 * Reusable States Input Component
 * Automatically fetches and displays states in a react-select dropdown
 * Returns full state object with id and abbreviation
 */
const StatesInput = memo(({
  name = "state",
  value = null,
  onChange,
  activeColor = "blue",
  placeholder = "Select State",
  isDisabled = false,
  isSearchable = true,
  isClearable = false,
  closeMenuOnSelect = true,
  inputId = null,
  className = "react-select",
  isLoading: externalLoading = false,
  countrySelected = true,
  customSelectStyles,
  styles: stylesProp,
  ...restProps
}) => {
  const { data: statesPayload, isLoading } = useGetStates();

  const statesList = useMemo(
    () => normalizeStatesFromApi(statesPayload),
    [statesPayload]
  );

  const statesOptions = useMemo(
    () =>
      statesList.map((state) => ({
        value: state.id,
        label: state.name,
        stateObject: state,
      })),
    [statesList]
  );

  const valueId = typeof value === "object" && value?.id ? value.id : value;
  const selectedOption = valueId
    ? statesOptions.find((option) => String(option.value) === String(valueId))
    : null;

  const handleChange = (option) => {
    if (!onChange) return;
    if (!option) {
      onChange(null);
      return;
    }
    const stateObject = option.stateObject;
    onChange({
      id: stateObject.id,
      abbreviation:
        stateObject?.abbreviation ||
        (typeof stateObject.name === "string"
          ? stateObject.name.substring(0, 2).toUpperCase()
          : ""),
      name: stateObject.name,
    });
  };

  const dynamicPlaceholder = !countrySelected
    ? "Select country first"
    : placeholder;

  const resolvedStyles = stylesProp ?? customSelectStyles;
  const mergedStyles = useMemo(() => {
    if (!resolvedStyles) return resolvedStyles;
    const baseInput = resolvedStyles.input;
    return {
      ...resolvedStyles,
      input: (provided, state) => {
        const merged = typeof baseInput === "function" ? baseInput(provided, state) : provided;
        return {
          ...merged,
          minWidth: "4px",
          opacity: 1,
        };
      },
    };
  }, [resolvedStyles]);

  return (
    <Select
      inputId={inputId || name}
      name={name}
      activeColor={activeColor}
      className={className}
      styles={mergedStyles}
      isSearchable={isSearchable}
      closeMenuOnSelect={closeMenuOnSelect}
      isClearable={isClearable}
      options={statesOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={dynamicPlaceholder}
      isDisabled={isDisabled || !countrySelected}
      isLoading={isLoading || externalLoading}
      filterOption={filterStateSelectOption}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching states" : "No states available"
      }
      {...restProps}
    />
  );
});

StatesInput.displayName = "StatesInput";

export default StatesInput;
